import { CommonModule } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    FormsModule,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms';
import {
    MatAutocomplete,
    MatAutocompleteModule,
} from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule, SubscriptSizing } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import {
    debounceTime,
    distinctUntilChanged,
    Subject,
    Subscription,
    switchMap,
    tap,
} from 'rxjs';
import { ApiService } from 'src/app/core/services/api.service';

interface Department {
  value: string;
  viewValue: string;
}

@Component({
  selector: 'app-department-select',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    MatInputModule,
    MatIconModule,
    FormsModule,
  ],
  templateUrl: './department-select.component.html',
  styleUrls: ['./department-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DepartmentSelectComponent,
      multi: true,
    },
  ],
})
export class DepartmentSelectComponent
  implements ControlValueAccessor, OnInit, OnChanges, OnDestroy
{
  @Input() required = false;
  @Input() disabled = false;
  @Input() formControl!: FormControl;
  @Input() selectedValue;

  /*
   * Forwarded to the field so callers can drop the reserved validation-message
   * row. Inside a form that row keeps the layout from jumping when an error
   * appears; on a toolbar it just offsets the field against its neighbours.
   */
  @Input() subscriptSizing: SubscriptSizing = 'fixed';
  @Output() departmentSelected = new EventEmitter<string>();
  @ViewChild('auto', { static: false }) autocomplete!: MatAutocomplete;
  private hasUserModified = false;
  value: string | null = null;
  searchText: string = '';
  filteredDepartments: Department[] = [];
  private search$ = new Subject<string>();
  private searchSubscription?: Subscription;
  private panelScrollHandler?: (event: any) => void;
  onChange = (_: any) => {};
  onTouched = () => {};

  departments: Department[] = [];
  isLoading = false;
  hasMore = true;
  private currentPage = 1;
  private pageSize = 10;
  private searchQuery = '';
  private totalItems = 0;

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
  ) {}

  writeValue(value: string): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  ngOnInit(): void {
    this.loadDepartments();

    this.searchSubscription = this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((value) => {
          this.currentPage = 1;
          this.hasMore = true;
          this.searchQuery = value;
          this.isLoading = true;
        }),
        switchMap(() =>
          this.apiService.request({
            path: 'departments/dropdown',
            method: 'GET',
            query: {
              limit: this.pageSize,
              page: this.currentPage,
              ...(this.searchQuery ? { q: this.searchQuery } : {}),
            },
          }),
        ),
      )
      .subscribe({
        next: (data: any) => {
          const newDepartments = data.items.map((m: any) => ({
            value: m.id,
            viewValue: m.name,
          }));

          this.departments = newDepartments;
          this.filteredDepartments = this.departments;

          this.totalItems = data.meta?.totalItems || 0;
          this.hasMore = this.departments.length < this.totalItems;

          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  onAutocompleteOpened(): void {
    setTimeout(() => {
      if (this.autocomplete && this.autocomplete.panel) {
        const panel = this.autocomplete.panel.nativeElement as HTMLElement;

        this.panelScrollHandler = (event: any) => {
          const scrollTop = event.target.scrollTop;
          const scrollHeight = event.target.scrollHeight;
          const clientHeight = event.target.clientHeight;
          const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

          // Load more when 80% scrolled
          if (scrollPercentage > 0.8 && this.hasMore && !this.isLoading) {
            this.loadMore();
          }
        };

        panel.addEventListener('scroll', this.panelScrollHandler);
      }
    });
  }

  ngOnDestroy(): void {
    try {
      if (this.panelScrollHandler && this.autocomplete?.panel) {
        const panel = this.autocomplete.panel.nativeElement as HTMLElement;
        panel.removeEventListener('scroll', this.panelScrollHandler);
      }
    } catch {}
    try {
      this.searchSubscription?.unsubscribe();
    } catch {}
  }

  selectDepartment(value: Department) {
    this.hasUserModified = false;
    const deptValue = value.value;
    this.formControl?.setValue(deptValue);
    this.formControl?.markAsTouched();
    this.departmentSelected.emit(deptValue);
    this.searchText = value.viewValue;
  }

  filterDepartments(filterValue: any): void {
    /*
     * Picking an option makes the autocomplete write the whole option object
     * back through the input's model, which lands here as a change like any
     * keystroke. Searching for the name of the option just chosen would narrow
     * the list down to that single row, so the next time the panel opens it
     * offers only the current choice. Take the label for the field and stop:
     * the loaded list stays whole and is ready as soon as the text is cleared.
     */
    if (filterValue && typeof filterValue !== 'string') {
      this.searchText = filterValue.viewValue || '';
      return;
    }

    this.hasUserModified = true;

    const value = filterValue || '';

    this.searchText = value;

    if (!value) {
      this.value = null;
      this.formControl?.setValue(null);
      this.departmentSelected.emit(null);

      this.search$.next('');
      return;
    }

    this.search$.next(value);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.hasUserModified) return;
    if (
      changes['formControl'] &&
      this.formControl &&
      this.departments.length > 0
    ) {
      const value = this.formControl.value;
      if (value) {
        this.setSelectedDepartmentById(value);
      }
    }

    // If selectedValue is passed, find and display that department
    if (changes['selectedValue'] && this.selectedValue) {
      this.setSelectedDepartmentById(this.selectedValue);
    }
  }

  displayDepartment(dept: Department | string): string {
    if (!dept) return '';
    return typeof dept === 'string' ? dept : dept.viewValue;
  }

  private setSelectedDepartmentById(departmentId: string) {
    if (!departmentId) return;

    const found = this.departments.find((d) => d.value === departmentId);

    if (found) {
      this.searchText = found.viewValue;
      this.value = departmentId;
    } else {
      this.fetchDepartmentById(departmentId);
    }
  }

  private fetchDepartmentById(departmentId: string) {
    const query: any = {
      id: departmentId,
    };

    this.apiService
      .request({
        path: 'departments/dropdown',
        method: 'GET',
        query,
      })
      .subscribe({
        next: (data: any) => {
          if (data.items && data.items.length > 0) {
            const deptData = data.items[0];
            const dept: Department = {
              value: deptData.id,
              viewValue: deptData.name,
            };
            // Add to list if not already there
            if (!this.departments.find((d) => d.value === deptData.id)) {
              this.departments = [dept, ...this.departments];
            }
            this.searchText = deptData.name;
            this.value = deptData.id;
            this.cdr.markForCheck();
          }
        },
        error: (err) => {
          console.error('Error loading department by ID', err);
        },
      });
  }

  private loadDepartments() {
    this.isLoading = true;
    const query: any = {
      limit: this.pageSize,
      page: this.currentPage,
    };

    if (this.searchQuery) {
      query.q = this.searchQuery;
    }

    this.apiService
      .request({ path: 'departments/dropdown', method: 'GET', query })
      .subscribe({
        next: (data: any) => {
          const newDepartments = data.items.map((m: any) => ({
            value: m.id,
            viewValue: m.name,
          }));

          // Append to existing list for pagination
          if (this.currentPage === 1) {
            this.departments = newDepartments;
          } else {
            this.departments = [...this.departments, ...newDepartments];
          }

          // Check if there are more items using meta information
          this.totalItems = data.meta?.totalItems || 0;
          const loadedCount = this.departments.length;
          this.hasMore = loadedCount < this.totalItems;

          this.filteredDepartments = this.departments;
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading departments', err);
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  loadMore() {
    this.currentPage++;
    this.loadDepartments();
  }
}
